import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BellRing,
  ChevronDown,
  History,
  Loader2,
  MessagesSquare,
  PanelRightClose,
  PanelRightOpen,
  RotateCcw,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  createPlaygroundSession,
  getPlaygroundReminderCycle,
  getPlaygroundRun,
  listPlaygroundMessages,
  PLAYGROUND_ROLES,
  sendPlaygroundMessage,
  simulatePlaygroundReminder,
  type CreatePlaygroundSessionInput,
  type PlaygroundListingAttachment,
  type PlaygroundMessage,
  type PlaygroundMessageAttachment,
  type PlaygroundRole,
} from '@/entities/playground';
import { listPlaygroundSessions, type Remark } from '@/entities/remarks';
import { ApiError } from '@/shared/api';
import { PageHeader } from '@/widgets/page-header';
import {
  PlaygroundDiagnosticsPanel,
  type PlaygroundDiagnosticsTab,
} from '@/widgets/playground-diagnostics';
import { PlaygroundReviewChat } from '@/widgets/playground-review-chat';
import { Avatar, AvatarFallback } from '@/shared/ui/shadcn/avatar';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { Bubble, BubbleContent } from '@/shared/ui/shadcn/bubble';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Marker, MarkerContent } from '@/shared/ui/shadcn/marker';
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
  MessageHeader,
} from '@/shared/ui/shadcn/message';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from '@/shared/ui/shadcn/message-scroller';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/shadcn/popover';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { ListingCard } from '@/shared/ui/listing-card';
import { splitMessageByListings } from '@/shared/lib/listing-anchors';
import { cn } from '@/shared/lib/utils';

const ROLE_LABELS: Record<PlaygroundRole, string> = {
  buyer: 'Buyer (auto-detect)',
  agent: 'Agent / broker',
  owner: 'Property owner',
  other: 'Other request',
};

function senderLabel(sender: string) {
  if (sender === 'interlocutor') return 'You';
  if (sender === 'agent') return 'ARIA';
  if (sender === 'user') return 'Operator';
  if (sender === 'system') return 'System';
  return sender;
}

function systemKind(attachments?: PlaygroundMessageAttachment[]): string {
  const marker = (attachments ?? []).find((item) => item?.type === 'system');
  const kind = marker && 'kind' in marker ? marker.kind : undefined;
  return typeof kind === 'string' ? kind : 'info';
}

const systemKindLabel: Record<string, string> = {
  crm_shadow: 'CRM shadow',
  reminder: 'Reminder',
  routing: 'Routing',
  info: 'System',
};

function SystemLine({ message }: { message: PlaygroundMessage }) {
  const kind = systemKind(message.attachments);
  return (
    <div
      data-playground-message={message.id}
      className="mx-auto flex w-full max-w-[85%] items-start gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground"
    >
      <Badge
        variant="outline"
        className="shrink-0 border-amber-500/50 font-normal text-amber-700 dark:text-amber-300"
      >
        {systemKindLabel[kind] ?? systemKindLabel.info}
      </Badge>
      <span className="min-w-0 whitespace-pre-wrap break-words">{message.content}</span>
    </div>
  );
}

function senderInitials(sender: string) {
  if (sender === 'interlocutor') return 'You';
  if (sender === 'agent') return 'AI';
  return 'Op';
}

function MessageBody({
  content,
  attachments,
}: {
  content: string;
  attachments?: PlaygroundMessageAttachment[];
}) {
  const listings = (attachments ?? []).filter(
    (item): item is PlaygroundListingAttachment =>
      item?.type === 'listing' && typeof item.id === 'string' && item.id.length > 0,
  );
  const segments = splitMessageByListings(content, listings);
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      {segments.map((segment, index) =>
        segment.kind === 'listing' ? (
          // Opening a listing must not also switch the selected run.
          <div
            key={`listing-${segment.listing.id}`}
            className="min-w-0"
            onClickCapture={(e) => e.stopPropagation()}
          >
            <ListingCard
              title={segment.listing.title}
              url={segment.listing.url}
              photos={segment.listing.photos}
              priceThb={
                segment.listing.price_thb != null ? String(segment.listing.price_thb) : undefined
              }
              usdPrice={
                segment.listing.usd_price != null ? String(segment.listing.usd_price) : undefined
              }
              priceQuote={
                segment.listing.price_quote != null
                  ? String(segment.listing.price_quote)
                  : undefined
              }
              quoteCurrency={segment.listing.quote_currency}
              district={segment.listing.district}
              areaSqm={
                segment.listing.area_sqm != null ? String(segment.listing.area_sqm) : undefined
              }
              propertyType={segment.listing.property_type}
            />
          </div>
        ) : (
          <span key={`text-${index}`} className="min-w-0 break-words whitespace-pre-wrap">
            {segment.text}
          </span>
        ),
      )}
    </div>
  );
}

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function RecentSessions({
  currentDialogId,
  onSelect,
}: {
  currentDialogId: string | null;
  onSelect: (dialogId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const sessionsQuery = useQuery({
    queryKey: ['playground-sessions'],
    queryFn: () => listPlaygroundSessions(40),
    enabled: open,
  });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={<Button size="sm" variant="outline" />}>
        <History className="size-4" />
        Sessions
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] p-0">
        <div className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
          Recent playground sessions
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {sessionsQuery.isLoading ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </div>
          ) : null}
          {(sessionsQuery.data ?? [])
            .filter((session) => session.message_count > 0)
            .map((session) => (
              <button
                key={session.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelect(session.id);
                }}
                className={cn(
                  'flex w-full flex-col gap-1 border-b px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted/60',
                  session.id === currentDialogId && 'bg-muted/50',
                )}
              >
                <span className="line-clamp-2 text-sm text-foreground">
                  {session.first_message || '(no messages)'}
                </span>
                <span className="flex flex-wrap items-center gap-2 text-muted-foreground">
                  <span>{formatRelative(session.last_activity_at)}</span>
                  <span>· {session.message_count} msgs</span>
                  {session.remarks_total > 0 ? (
                    <Badge
                      variant={session.remarks_open > 0 ? 'destructive' : 'secondary'}
                      className="font-normal"
                    >
                      {session.remarks_open}/{session.remarks_total} remarks
                    </Badge>
                  ) : null}
                </span>
              </button>
            ))}
          {sessionsQuery.data && sessionsQuery.data.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">No sessions yet.</p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function PlaygroundPage() {
  const { dialogId: routeDialogId } = useParams<{ dialogId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const dialogId = routeDialogId ?? null;
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<PlaygroundMessage[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(searchParams.get('run'));
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(
    searchParams.get('message'),
  );
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(true);
  const [panelTab, setPanelTab] = useState<PlaygroundDiagnosticsTab>(
    searchParams.get('tab') === 'remarks' ? 'remarks' : 'thinking',
  );
  const scrolledToRef = useRef<string | null>(null);

  const sessionMutation = useMutation({
    mutationFn: (input: CreatePlaygroundSessionInput = {}) => createPlaygroundSession(input),
    onSuccess: (session) => {
      setMessages([]);
      setActiveRunId(null);
      setHighlightedMessageId(null);
      navigate(`/playground/${session.dialog_id}`, { replace: true });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'Failed to start session');
    },
  });

  useEffect(() => {
    if (!dialogId && !sessionMutation.isPending && !sessionMutation.isError) {
      sessionMutation.mutate({});
    }
    // Bootstrap a session only when the route has none.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogId]);

  const historyQuery = useQuery({
    queryKey: ['playground-messages', dialogId],
    queryFn: () => listPlaygroundMessages(dialogId!),
    enabled: Boolean(dialogId),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (historyQuery.data) {
      setMessages(historyQuery.data);
      if (!activeRunId) {
        const lastAgent = [...historyQuery.data].reverse().find((m) => m.run_id);
        if (lastAgent?.run_id) setActiveRunId(lastAgent.run_id);
      }
    }
    // Only sync on fresh history load; sends update messages directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyQuery.data]);

  useEffect(() => {
    if (historyQuery.error) {
      toast.error(
        historyQuery.error instanceof ApiError
          ? historyQuery.error.message
          : 'Failed to load dialog',
      );
    }
  }, [historyQuery.error]);

  useEffect(() => {
    if (!highlightedMessageId || messages.length === 0) return;
    if (scrolledToRef.current === highlightedMessageId) return;
    const node = document.querySelector<HTMLElement>(
      `[data-playground-message="${highlightedMessageId}"]`,
    );
    if (node) {
      node.scrollIntoView({ block: 'center' });
      scrolledToRef.current = highlightedMessageId;
    }
  }, [highlightedMessageId, messages]);

  const sendMutation = useMutation({
    mutationFn: (content: string) => {
      if (!dialogId) {
        return Promise.reject(new Error('No session'));
      }
      return sendPlaygroundMessage(dialogId, {
        content,
        idempotency_key: crypto.randomUUID(),
      });
    },
    onSuccess: async (result) => {
      setMessages(result.messages);
      setDraft('');
      setHighlightedMessageId(null);
      if (result.run_id) {
        setActiveRunId(result.run_id);
        setDiagnosticsOpen(true);
      }
      if (result.ai_skipped_pending_approval) {
        toast.message('AI skipped — a reply is still pending approval');
      }
      queryClient.setQueryData(['playground-messages', dialogId], result.messages);
      await queryClient.invalidateQueries({ queryKey: ['playground-sessions'] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'Send failed');
    },
  });

  const runQuery = useQuery({
    queryKey: ['playground-run', dialogId, activeRunId],
    queryFn: () => getPlaygroundRun(dialogId!, activeRunId!),
    enabled: Boolean(dialogId && activeRunId),
  });

  const reminderQuery = useQuery({
    queryKey: ['playground-reminders', dialogId, messages.length],
    queryFn: () => getPlaygroundReminderCycle(dialogId!),
    enabled: Boolean(dialogId),
  });

  const simulateReminderMutation = useMutation({
    mutationFn: () => {
      if (!dialogId) {
        return Promise.reject(new Error('No session'));
      }
      return simulatePlaygroundReminder(dialogId);
    },
    onSuccess: async (result) => {
      setMessages(result.messages);
      queryClient.setQueryData(['playground-messages', dialogId], result.messages);
      await queryClient.invalidateQueries({ queryKey: ['playground-reminders', dialogId] });
      toast.message(
        result.step.kind === 'push'
          ? `No-reply push ${result.step.number}/3 added as an ARIA message (+${result.step.offset})`
          : 'Lead moved to «Отложенный спрос»',
      );
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.message('No active reminder series — ARIA has to ask a question first');
        return;
      }
      toast.error(err instanceof ApiError ? err.message : 'Simulation failed');
    },
  });

  const reminderActive = reminderQuery.data?.status === 'active';

  const busy =
    sessionMutation.isPending || sendMutation.isPending || historyQuery.isLoading || !dialogId;

  const selectRun = (runId: string | null | undefined, messageId?: string) => {
    if (!runId) return;
    setActiveRunId(runId);
    setHighlightedMessageId(messageId ?? null);
    setDiagnosticsOpen(true);
    setPanelTab('thinking');
    const next = new URLSearchParams(searchParams);
    next.set('run', runId);
    if (messageId) next.set('message', messageId);
    else next.delete('message');
    setSearchParams(next, { replace: true });
  };

  const focusRemark = (remark: Remark) => {
    if (remark.run_id) {
      setActiveRunId(remark.run_id);
    }
    setHighlightedMessageId(remark.message_id ?? null);
    scrolledToRef.current = null;
    setDiagnosticsOpen(true);
  };

  const openDialog = (nextDialogId: string) => {
    if (nextDialogId === dialogId) return;
    setMessages([]);
    setActiveRunId(null);
    setHighlightedMessageId(null);
    scrolledToRef.current = null;
    navigate(`/playground/${nextDialogId}`);
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PageHeader
        title="Playground"
        description="Chat as a marketplace visitor against the live reply pipeline"
        actions={
          <div className="flex items-center gap-2">
            <RecentSessions currentDialogId={dialogId} onSelect={openDialog} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDiagnosticsOpen((value) => !value)}
            >
              {diagnosticsOpen ? (
                <PanelRightClose className="size-4" />
              ) : (
                <PanelRightOpen className="size-4" />
              )}
              Diagnostics
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || simulateReminderMutation.isPending || !reminderActive}
              title={
                reminderActive
                  ? `Simulate no-reply step ${(reminderQuery.data?.pushes_sent ?? 0) + 1}`
                  : 'No active reminder series — ARIA has to ask a question first'
              }
              onClick={() => simulateReminderMutation.mutate()}
            >
              {simulateReminderMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <BellRing className="size-4" />
              )}
              Simulate no-reply
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={sessionMutation.isPending}
                render={<Button type="button" variant="outline" size="sm" />}
              >
                <RotateCcw className="size-4" />
                New chat
                <ChevronDown data-icon="inline-end" className="size-3.5 opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                {PLAYGROUND_ROLES.map((role) => (
                  <DropdownMenuItem
                    key={role}
                    disabled={sessionMutation.isPending}
                    onClick={() => sessionMutation.mutate(role === 'buyer' ? {} : { role })}
                  >
                    {ROLE_LABELS[role]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-4 pt-0">
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <MessageScrollerProvider autoScroll={!highlightedMessageId}>
              <MessageScroller className="absolute inset-0 min-h-0">
                <MessageScrollerViewport>
                  <MessageScrollerContent className="gap-4 p-4">
                    {historyQuery.isLoading ? (
                      <Marker className="py-8">
                        <Loader2 className="size-4 animate-spin" />
                        <MarkerContent>Loading dialog</MarkerContent>
                      </Marker>
                    ) : null}
                    {messages.length === 0 && !sendMutation.isPending && !historyQuery.isLoading ? (
                      <Marker variant="separator" className="py-8">
                        <MarkerContent className="inline-flex items-center gap-2">
                          <MessagesSquare className="size-4" />
                          Send a message to start
                        </MarkerContent>
                      </Marker>
                    ) : null}
                    {messages.map((m) => {
                      if (m.sender === 'system') {
                        return (
                          <MessageScrollerItem key={m.id} messageId={m.id}>
                            <SystemLine message={m} />
                          </MessageScrollerItem>
                        );
                      }
                      const isVisitor = m.sender === 'interlocutor';
                      const align = isVisitor ? 'end' : 'start';
                      const selectable = Boolean(m.run_id);
                      const isActiveRun = selectable && m.run_id === activeRunId;
                      const isHighlighted = m.id === highlightedMessageId;
                      return (
                        <MessageScrollerItem key={m.id} messageId={m.id} scrollAnchor={isVisitor}>
                          <div data-playground-message={m.id}>
                            <Message align={align}>
                              <MessageAvatar>
                                <Avatar className="size-8">
                                  <AvatarFallback className="text-[10px]">
                                    {senderInitials(m.sender)}
                                  </AvatarFallback>
                                </Avatar>
                              </MessageAvatar>
                              <MessageContent>
                                <div
                                  className={cn(
                                    'flex w-max max-w-[80%] flex-col gap-2.5',
                                    isVisitor && 'ml-auto items-end',
                                  )}
                                >
                                  <MessageHeader>{senderLabel(m.sender)}</MessageHeader>
                                  <div
                                    role={selectable ? 'button' : undefined}
                                    tabIndex={selectable ? 0 : undefined}
                                    className={cn(
                                      'block w-max max-w-full rounded-xl text-left',
                                      selectable && 'cursor-pointer',
                                      isHighlighted &&
                                        'ring-2 ring-amber-500/70 ring-offset-2 ring-offset-background',
                                    )}
                                    onClick={() => selectRun(m.run_id, m.id)}
                                    onKeyDown={(e) => {
                                      if (e.key !== 'Enter' && e.key !== ' ') return;
                                      e.preventDefault();
                                      selectRun(m.run_id, m.id);
                                    }}
                                  >
                                    <Bubble
                                      align={align}
                                      variant={isVisitor ? 'default' : 'secondary'}
                                      className={cn(
                                        'max-w-full min-w-min',
                                        isActiveRun &&
                                          '*:data-[slot=bubble-content]:bg-blue-500/25 *:data-[slot=bubble-content]:text-foreground dark:*:data-[slot=bubble-content]:bg-blue-500/30',
                                      )}
                                    >
                                      <BubbleContent>
                                        <MessageBody
                                          content={m.content}
                                          attachments={m.attachments}
                                        />
                                      </BubbleContent>
                                    </Bubble>
                                  </div>
                                  {m.sender === 'agent' && m.lifecycle_status !== 'sent' ? (
                                    <MessageFooter>{m.lifecycle_status}</MessageFooter>
                                  ) : null}
                                </div>
                              </MessageContent>
                            </Message>
                          </div>
                        </MessageScrollerItem>
                      );
                    })}
                    {sendMutation.isPending ? (
                      <MessageScrollerItem messageId="typing">
                        <Marker>
                          <Loader2 className="size-4 animate-spin" />
                          <MarkerContent>Thinking</MarkerContent>
                        </Marker>
                      </MessageScrollerItem>
                    ) : null}
                  </MessageScrollerContent>
                </MessageScrollerViewport>
                <MessageScrollerButton />
              </MessageScroller>
            </MessageScrollerProvider>
          </div>

          <form
            className="flex shrink-0 items-stretch gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const content = draft.trim();
              if (!content || busy) return;
              sendMutation.mutate(content);
            }}
          >
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask as a buyer…"
              disabled={busy}
              rows={2}
              className="min-h-[44px] flex-1 resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const content = draft.trim();
                  if (!content || busy) return;
                  sendMutation.mutate(content);
                }
              }}
            />
            <Button type="submit" disabled={busy || !draft.trim()} size="lg" className="h-auto">
              {sendMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              Send
            </Button>
          </form>
        </div>

        {diagnosticsOpen ? (
          <PlaygroundDiagnosticsPanel
            dialogId={dialogId}
            memoryRevision={activeRunId}
            run={runQuery.data}
            isLoading={runQuery.isFetching}
            errorMessage={
              runQuery.error instanceof ApiError
                ? runQuery.error.message
                : runQuery.error
                  ? 'Failed to load run'
                  : null
            }
            tab={panelTab}
            onTabChange={setPanelTab}
            remarksContent={
              <PlaygroundReviewChat
                dialogId={dialogId}
                revision={activeRunId}
                onSelectRemark={focusRemark}
              />
            }
          />
        ) : null}
      </div>
    </div>
  );
}
